import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Stack,
  Alert,
  Chip,
  Divider,
  Grid,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { FormControlLabel, Checkbox } from '@mui/material';
import {
  AttachMoney,
  CheckCircle,
  Edit,
  Send,
  Close as CloseIcon,
} from '@mui/icons-material';
import { contractorRespondQuote } from '../services/contractorJobService';
import { getContractorJobPhotoUrl } from '../utils/photoUtils';
import { useNavigate } from 'react-router-dom';

interface JobDetailsCardProps {
  job: any;
  property: any;
  onQuoteSubmitted?: (quote: { amount: number; notes?: string }) => void;
}

export const JobDetailsCard: React.FC<JobDetailsCardProps> = ({
  job,
  property,
  onQuoteSubmitted,
}) => {
  const navigate = useNavigate();
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteNotes, setQuoteNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [gstInclusive, setGstInclusive] = useState(false);
  
  // Photo dialog state
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<any>(null);
  

  const isQuoteRequest = job.quoteStatus && job.quoteStatus !== 'accepted';
  const hasAgencyQuote = job.agencyQuote && job.agencyQuote.amount > 0;
  const hasContractorQuote = job.contractorQuote && job.contractorQuote.amount > 0;

  // Photo dialog handlers
  const handlePhotoClick = (photo: any) => {
    setSelectedPhoto(photo);
    setPhotoDialogOpen(true);
  };


  const handleClosePhotoDialog = () => {
    setPhotoDialogOpen(false);
    setSelectedPhoto(null);
  };

  const getPhotoUrlForDisplay = (photo: any) => {
    return getContractorJobPhotoUrl(photo, job?.agencySlug);
  };

  const handleSubmitQuote = async () => {
    if (!quoteAmount || isNaN(parseFloat(quoteAmount))) {
      return;
    }

    // Check if contractor is authenticated
    const contractorToken = localStorage.getItem('contractor_token');
    if (!contractorToken) {
      // Redirect to authentication page
      const authUrl = `/contractor/auth?token=${job.accessToken}&redirect=/jobs/${job.accessToken}`;
      navigate(authUrl);
      return;
    }

    try {
      setSubmitting(true);
      const amountNum = parseFloat(quoteAmount);
      await contractorRespondQuote(job.accessToken, {
        amount: amountNum,
        notes: quoteNotes || undefined,
        gstInclusive,
      });
      
      setQuoteAmount('');
      setQuoteNotes('');
      
      if (onQuoteSubmitted) {
        onQuoteSubmitted({ amount: amountNum, notes: quoteNotes || undefined });
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptAgencyQuote = async () => {
    try {
      setSubmitting(true);
      await contractorRespondQuote(job.accessToken, {
        amount: job.agencyQuote.amount,
        notes: 'Accepted agency quote',
      });
      
      if (onQuoteSubmitted) {
        onQuoteSubmitted({ amount: job.agencyQuote.amount, notes: 'Accepted agency quote' });
      }
    } catch (error) {
      console.error('Error accepting agency quote:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(amount);
  };

  return (
    <>
      <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          📋 Job Details
        </Typography>
        
        <Typography variant="h5" gutterBottom>
          {job.contractorType.charAt(0).toUpperCase() + job.contractorType.slice(1)} Job
        </Typography>
        
        <Typography variant="body1" paragraph>
          <strong>Property:</strong> {
            typeof property.address === 'object' 
              ? `${property.address.street}, ${property.address.city}, ${property.address.state} ${property.address.postalCode}`
              : `${property.address}, ${property.city}, ${property.state} ${property.zip}`
          }
        </Typography>

        {job.notes && (
          <Typography variant="body1" paragraph>
            <strong>Job Description:</strong> {job.notes}
          </Typography>
        )}

        {(job.startDate || job.finishDate) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {job.startDate && (
              <Typography variant="body1">
                <strong>Start Date:</strong> {
                  (() => {
                    const startDate = new Date(job.startDate);
                    // Check UTC time components - if all are 0, it's likely date-only
                    const utcHours = startDate.getUTCHours();
                    const utcMinutes = startDate.getUTCMinutes();
                    const utcSeconds = startDate.getUTCSeconds();
                    const isMidnightUTC = utcHours === 0 && utcMinutes === 0 && utcSeconds === 0;
                    
                    // Check local time components as well
                    const localHours = startDate.getHours();
                    const localMinutes = startDate.getMinutes();
                    const localSeconds = startDate.getSeconds();
                    const isMidnightLocal = localHours === 0 && localMinutes === 0 && localSeconds === 0;
                    
                    // Show time if it's not midnight in both UTC and local time
                    // (accounting for timezone differences)
                    if (!isMidnightUTC || !isMidnightLocal) {
                      return startDate.toLocaleString('en-AU', { 
                        weekday: 'long',
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                    } else {
                      return startDate.toLocaleDateString('en-AU', { 
                        weekday: 'long',
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric'
                      });
                    }
                  })()
                }
              </Typography>
            )}
            {job.finishDate && (
              <Typography variant="body1">
                <strong>Finish Date:</strong> {
                  (() => {
                    const finishDate = new Date(job.finishDate);
                    // Check UTC time components - if all are 0, it's likely date-only
                    const utcHours = finishDate.getUTCHours();
                    const utcMinutes = finishDate.getUTCMinutes();
                    const utcSeconds = finishDate.getUTCSeconds();
                    const isMidnightUTC = utcHours === 0 && utcMinutes === 0 && utcSeconds === 0;
                    
                    // Check local time components as well
                    const localHours = finishDate.getHours();
                    const localMinutes = finishDate.getMinutes();
                    const localSeconds = finishDate.getSeconds();
                    const isMidnightLocal = localHours === 0 && localMinutes === 0 && localSeconds === 0;
                    
                    // Show time if it's not midnight in both UTC and local time
                    if (!isMidnightUTC || !isMidnightLocal) {
                      return finishDate.toLocaleString('en-AU', { 
                        weekday: 'long',
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                    } else {
                      return finishDate.toLocaleDateString('en-AU', { 
                        weekday: 'long',
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric'
                      });
                    }
                  })()
                }
              </Typography>
            )}
          </Box>
        )}



        {/* Quote Status and Actions */}
        {isQuoteRequest && (
          <Box>
            <Divider sx={{ my: 2 }} />
            
            {!hasAgencyQuote && !hasContractorQuote ? (
              // No budget provided - quote request
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    💰 Quote Request
                  </Typography>
                  <Typography variant="body2">
                    You have been invited to provide a quote for this job. Please submit your quote below.
                  </Typography>
                </Alert>
                
                <Stack spacing={2}>
                  <TextField
                    label="Your Quote Amount"
                    type="number"
                    value={quoteAmount}
                    onChange={(e) => setQuoteAmount(e.target.value)}
                    InputProps={{
                      startAdornment: <AttachMoney />,
                    }}
                    helperText="Enter your quote amount in AUD"
                    fullWidth
                  />
                  <FormControlLabel
                    control={<Checkbox checked={gstInclusive} onChange={(e) => setGstInclusive(e.target.checked)} />}
                    label="Amount includes GST"
                  />
                  
                  <TextField
                    label="Notes (Optional)"
                    multiline
                    rows={3}
                    value={quoteNotes}
                    onChange={(e) => setQuoteNotes(e.target.value)}
                    placeholder="Add any additional details about your quote..."
                    fullWidth
                  />
                  
                  <Button
                    variant="contained"
                    onClick={handleSubmitQuote}
                    disabled={!quoteAmount || submitting}
                    startIcon={<Send />}
                    fullWidth
                  >
                    {submitting ? 'Submitting Quote...' : 'Submit Quote'}
                  </Button>
                </Stack>
              </Box>
            ) : !hasAgencyQuote && hasContractorQuote ? (
              // Contractor has submitted quote but no agency budget yet
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    ✅ Quote Submitted
                  </Typography>
                  <Typography variant="body2">
                    Thank you! Your quote has been submitted and is being reviewed by the agency. You will be notified once a decision is made.
                  </Typography>
                </Alert>
                
                <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="h6" color="success.dark">
                    {formatCurrency(job.contractorQuote.amount)}
                  </Typography>
                  {job.contractorQuote.notes && (
                    <Typography variant="body2" color="text.secondary">
                      {job.contractorQuote.notes}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {(() => {
                      const ts = (job.contractorQuote as any)?.sentAt || (job.contractorQuote as any)?.submittedAt;
                      const d = ts ? new Date(ts) : null;
                      return `Submitted: ${d && !isNaN(d.getTime()) ? d.toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Unknown'}`;
                    })()}
                  </Typography>
                </Box>
              </Box>
            ) : hasAgencyQuote && !hasContractorQuote ? (
              // Agency provided budget - accept or renegotiate
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    💰 Agency Budget Provided
                  </Typography>
                  <Typography variant="body2">
                    The agency has provided a budget for this job.
                  </Typography>
                </Alert>
                
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(job.agencyQuote.amount)}
                  </Typography>
                  {job.agencyQuote.notes && (
                    <Typography variant="body2" color="text.secondary">
                      {job.agencyQuote.notes}
                    </Typography>
                  )}
                </Box>
                
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleAcceptAgencyQuote}
                    disabled={submitting}
                    startIcon={<CheckCircle />}
                    sx={{ flex: 1 }}
                  >
                    {submitting ? 'Accepting...' : 'Accept Quote'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setQuoteAmount(job.agencyQuote.amount.toString());
                      setQuoteNotes('Counter offer to agency quote');
                    }}
                    startIcon={<Edit />}
                    sx={{ flex: 1 }}
                  >
                    Re-negotiate
                  </Button>
                </Stack>
                
                {/* Counter offer form (shown when re-negotiate is clicked) */}
                {quoteAmount && (
                  <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Counter Offer
                    </Typography>
                    <Stack spacing={2}>
                      <TextField
                        label="Your Counter Offer"
                        type="number"
                        value={quoteAmount}
                        onChange={(e) => setQuoteAmount(e.target.value)}
                        InputProps={{
                          startAdornment: <AttachMoney />,
                        }}
                        fullWidth
                      />
                      
                      <TextField
                        label="Notes"
                        multiline
                        rows={2}
                        value={quoteNotes}
                        onChange={(e) => setQuoteNotes(e.target.value)}
                        fullWidth
                      />
                      
                      <Button
                        variant="contained"
                        onClick={handleSubmitQuote}
                        disabled={!quoteAmount || submitting}
                        startIcon={<Send />}
                        fullWidth
                      >
                        {submitting ? 'Submitting Counter Offer...' : 'Submit Counter Offer'}
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Box>
            ) : hasContractorQuote ? (
              // Contractor has already responded
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    ✅ Quote Submitted
                  </Typography>
                  <Typography variant="body2">
                    Thank you! Your quote has been submitted and is being reviewed by the agency. You will be notified once a decision is made.
                  </Typography>
                </Alert>
                
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(job.contractorQuote.amount)}
                  </Typography>
                  {job.contractorQuote.notes && (
                    <Typography variant="body2" color="text.secondary">
                      {job.contractorQuote.notes}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Submitted: {new Date(job.contractorQuote.submittedAt || job.contractorQuote.sentAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </Typography>
                </Box>
                
                <Chip
                  label="Awaiting Agency Response"
                  color="warning"
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : null}
          </Box>
        )}

        {/* Show accepted quote if available */}
        {job.acceptedQuote && (
          <Box>
            <Divider sx={{ my: 2 }} />
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                ✅ Quote Accepted
              </Typography>
              <Typography variant="body2">
                Your quote has been accepted by the agency.
              </Typography>
            </Alert>
            
            <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
              <Typography variant="h6" color="success.dark">
                {formatCurrency(job.acceptedQuote.amount)}
              </Typography>
              {job.acceptedQuote.notes && (
                <Typography variant="body2" color="text.secondary">
                  {job.acceptedQuote.notes}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Accepted: {new Date(job.acceptedQuote.decidedAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>

    {/* Photo Dialog */}
    <Dialog
      open={photoDialogOpen}
      onClose={handleClosePhotoDialog}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          color: 'white',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ color: 'white' }}>
          {selectedPhoto?.filename || 'Photo'}
        </Typography>
        <IconButton onClick={handleClosePhotoDialog} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {selectedPhoto && (
          <img
            src={getPhotoUrlForDisplay(selectedPhoto)}
            alt={selectedPhoto.filename || 'Photo'}
            style={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain',
            }}
          />
        )}
      </DialogContent>
      {selectedPhoto && (
        <DialogActions sx={{ justifyContent: 'space-between', padding: 2 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Uploaded: {new Date(selectedPhoto.uploadedAt).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </Typography>
          <Button
            onClick={() => window.open(getPhotoUrlForDisplay(selectedPhoto), '_blank')}
            variant="outlined"
            sx={{ color: 'white', borderColor: 'white' }}
          >
            Open in New Tab
          </Button>
        </DialogActions>
      )}
    </Dialog>
    </>
  );
};

